import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, IconButton, Alert, CircularProgress, Chip,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PageHeader from './PageHeader';

export default function CrudPage({
  title, subtitle, columns, fetchFn, addFn, editFn, deleteFn,
  FormFields, defaultFormState, formToPayload, canWrite = true,
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(defaultFormState);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await fetchFn();
      setRows((data.rows || []).map((r) => ({ ...r, id: r._id })));
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [fetchFn]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openAdd = () => { setEditRow(null); setForm(defaultFormState); setError(''); setDialogOpen(true); };
  const openEdit = (row) => { setEditRow(row); setForm(row); setError(''); setDialogOpen(true); };

  const handleSave = async () => {
    setSaving(true); setError('');
    try {
      const payload = formToPayload ? formToPayload(form) : form;
      if (editRow) { await editFn(editRow._id, payload); }
      else { await addFn(payload); }
      setDialogOpen(false);
      fetchData();
    } catch (e) { setError(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete "${row.title || row.name}"?`)) return;
    try { await deleteFn(row._id); fetchData(); }
    catch (e) { alert(e?.response?.data?.error || 'Delete failed'); }
  };

  const actionColumn = {
    field: 'actions', headerName: '', width: 100, sortable: false,
    renderCell: (p) => (
      <Box>
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); openEdit(p.row); }}>
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(p.row); }}>
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>
    ),
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title={title}
        subtitle={subtitle}
        action={canWrite
          ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}
              sx={{ bgcolor: '#fec958', '&:hover': { bgcolor: '#4f52d6' } }}>
              Add {title.replace(/s$/, '')}
            </Button>
          )
          : <Chip label="Read only" size="small" variant="outlined" />
        }
      />

      <DataGrid
        rows={rows}
        columns={canWrite ? [...columns, actionColumn] : columns}
        loading={loading}
        pageSizeOptions={[10, 25, 50]}
        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
        sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e8eaf0' }}
        autoHeight
        disableRowSelectionOnClick
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editRow ? `Edit ${title.replace(/s$/, '')}` : `Add ${title.replace(/s$/, '')}`}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <FormFields form={form} setForm={setForm} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}
            sx={{ bgcolor: '#fec958', '&:hover': { bgcolor: '#4f52d6' } }}>
            {saving ? <CircularProgress size={20} /> : (editRow ? 'Save' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
